import { useCategories, useCategoryPath } from '../../hooks/useCategories';
import { useInstallmentGroups } from '../../hooks/useInstallmentGroups';
import { useUpdateTransaction } from '../../hooks/useTransactions';
import { useAddCorrection } from '../../hooks/useCorrections';
import { useToast } from './ToastContext';
import { Modal } from './Modal';
import { CategorySelector } from './CategorySelector';
import { ExcludeFromBudgetCheckbox } from './ExcludeFromBudgetCheckbox';
import { formatDateShort, formatAmount } from '../../utils/format';
import { CARD_TYPE_LABELS } from '../../utils/constants';
import type { Transaction } from '../../types';

interface TransactionDetailModalProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
}

export function TransactionDetailModal({
  transaction,
  isOpen,
  onClose,
}: TransactionDetailModalProps) {
  const categories = useCategories();
  const categoryPath = useCategoryPath(transaction?.categoryId ?? '');
  const installmentGroups = useInstallmentGroups();
  const updateTransaction = useUpdateTransaction();
  const addCorrection = useAddCorrection();
  const { showToast } = useToast();

  if (!transaction) return null;

  const installmentGroup = transaction.installmentInfo?.groupId
    ? installmentGroups.find(
        (g) => g.groupId === transaction.installmentInfo?.groupId
      )
    : undefined;

  const handleCategoryChange = (newCategoryId: string) => {
    if (transaction.categoryId === newCategoryId) return;
    updateTransaction(transaction.id, { categoryId: newCategoryId });
    addCorrection(transaction, newCategoryId);
    showToast('Categoria actualizada.', 'success');
  };

  const handleExcludeChange = (excluded: boolean) => {
    updateTransaction(transaction.id, { excludedFromBudget: excluded });
    showToast(
      excluded
        ? 'Transaccion excluida del presupuesto.'
        : 'Transaccion incluida en el presupuesto.',
      'success'
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalle de transaccion">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-mid-gray">Fecha</p>
            <p className="font-medium text-near-black">
              {formatDateShort(transaction.date)}
            </p>
          </div>
          <div>
            <p className="text-sm text-mid-gray">Monto</p>
            <p
              className={`font-bold text-lg ${
                transaction.amount < 0 ? 'text-green-600' : 'text-near-black'
              }`}
            >
              {transaction.amount < 0 && '+ '}
              {formatAmount(transaction.amount)}
            </p>
          </div>
        </div>

        <div>
          <p className="text-sm text-mid-gray">Descripcion</p>
          <p className="font-medium text-near-black break-words">
            {transaction.description}
          </p>
        </div>

        <div>
          <p className="text-sm text-mid-gray mb-1">Categoria</p>
          {categoryPath.length > 0 && (
            <p className="text-xs text-mid-gray mb-2">
              {categoryPath.map((c) => c.name).join(' > ')}
            </p>
          )}
          <CategorySelector
            selectedCategoryId={transaction.categoryId}
            onSelect={handleCategoryChange}
            categories={categories}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-mid-gray">Tarjeta</p>
            <p className="text-near-black">
              {CARD_TYPE_LABELS[transaction.cardType]}
            </p>
            <p className="text-xs text-mid-gray">
              {transaction.cardIdentifier}
            </p>
          </div>
          <div>
            <p className="text-sm text-mid-gray">Tipo</p>
            <p className="text-near-black capitalize">{transaction.type}</p>
          </div>
        </div>

        {transaction.isInstallment && transaction.installmentInfo && (
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-700">
              Pago {transaction.installmentInfo.current} de{' '}
              {transaction.installmentInfo.total}
            </p>
            {installmentGroup && (
              <p className="text-sm text-blue-600 mt-1">
                Total original: {formatAmount(installmentGroup.originalAmount)}
              </p>
            )}
          </div>
        )}

        <div className="border-t border-light-gray pt-4">
          <ExcludeFromBudgetCheckbox
            excluded={transaction.excludedFromBudget ?? false}
            onChange={handleExcludeChange}
          />
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-brand-primary text-white rounded hover:opacity-90 transition min-h-[44px]"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
}
